import React, { useState, useEffect } from 'react';
import { generateClient } from 'aws-amplify/api';
import { FaSync, FaDownload, FaExclamationCircle, FaFilter, 
         FaList, FaFolderOpen, FaRobot } from 'react-icons/fa';
import { getFileTypeIcon } from '../../utils/formatters';
import SearchBar from './SearchBar';
import FolderStructure from './FolderStructure';
import AIOrganizer from './AIOrganizer';
import FileDetails from './FileDetails';
import '../../styles/Files.css';

const API = generateClient();

const FileList = ({ searchTerm: initialSearchTerm }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all', 'backed-up', 'pending', 'error'
  const [sortBy, setSortBy] = useState('name'); // 'name', 'date', 'status'
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc', 'desc'
  const [visibleFilters, setVisibleFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm || '');
  const [selectedFile, setSelectedFile] = useState(null);
  
  // New state for managing the file view
  const [viewMode, setViewMode] = useState('folders'); // 'list', 'folders', 'ai'
  
  useEffect(() => {
    if (initialSearchTerm !== undefined && initialSearchTerm !== searchTerm) {
      setSearchTerm(initialSearchTerm);
    }
  }, [initialSearchTerm]);
  
  useEffect(() => {
    fetchFiles();
  }, []);
  
  const fetchFiles = async () => {
    setLoading(true);
    try {
      // DEVELOPMENT ONLY: Use mock data instead of API call
      // Comment this section out when backend is ready
      const mockFiles = [
        {
          originalId: '123456',
          name: 'Dragon Statue.stl',
          creatorName: 'MakerDesigns',
          updatedAt: '2025-02-15T14:23:45Z',
          fileSize: 12582912 // 12MB
        },
        {
          originalId: '234567',
          name: 'Castle Tower.stl',
          creatorName: 'FantasyPrints',
          updatedAt: '2025-03-01T09:12:32Z',
          fileSize: 8388608 // 8MB
        },
        {
          originalId: '345678',
          name: 'Miniature Set - Heroes.zip',
          creatorName: 'MakerDesigns',
          updatedAt: '2025-03-10T22:45:12Z',
          fileSize: 31457280 // 30MB
        },
        {
          originalId: '456789',
          name: 'Terrain Pack.zip',
          creatorName: 'TerrainMaster',
          updatedAt: '2025-03-18T11:33:45Z',
          fileSize: 41943040 // 40MB
        },
        {
          originalId: '567890',
          name: 'Character Guide.pdf',
          creatorName: 'GameMasterTools',
          updatedAt: '2025-03-20T16:22:18Z',
          fileSize: 4194304 // 4MB
        },
        {
          originalId: '678901',
          name: 'Sci-Fi Spaceship.stl',
          creatorName: 'FuturePrints',
          updatedAt: '2025-03-22T10:15:30Z',
          fileSize: 15728640 // 15MB
        },
        {
          originalId: '789012',
          name: 'Fantasy Wizard.stl',
          creatorName: 'MakerDesigns',
          updatedAt: '2025-03-23T14:22:18Z',
          fileSize: 10485760 // 10MB
        },
        {
          originalId: '890123',
          name: 'Dungeon Props.zip',
          creatorName: 'TerrainMaster',
          updatedAt: '2025-03-24T09:45:20Z',
          fileSize: 20971520 // 20MB
        },
        {
          originalId: '901234',
          name: 'Robot Character.stl',
          creatorName: 'FuturePrints',
          updatedAt: '2025-03-25T16:30:10Z',
          fileSize: 9437184 // 9MB
        },
        {
          originalId: '012345',
          name: 'Mountain Terrain.stl',
          creatorName: 'TerrainMaster',
          updatedAt: '2025-03-26T11:20:45Z',
          fileSize: 14680064 // 14MB
        }
      ];
      
      setFiles(mockFiles);
      setLoading(false);
      return;
      // END MOCK DATA SECTION
      
      // UNCOMMENT THIS WHEN BACKEND IS READY:
      // const response = await API.get('mmfBackupApi', '/files', {});
      // setFiles(response.files || []);
    } catch(err) {
      console.error("Error fetching files", err);
      setError('Failed to load files');
      setLoading(false);
    }
  };
  
  const handleDownloadFile = async (fileId, fileName) => {
    try {
      // In a real implementation, this would call the MMF API to download the file
      // For now, we'll just show an alert
      alert(`Downloading file: ${fileName}`);
      
      // The real implementation would look something like this:
      // const response = await API.get('mmfBackupApi', `/files/download/${fileId}`, {
      //   responseType: 'blob'
      // });
      // 
      // // Create a download link
      // const url = window.URL.createObjectURL(new Blob([response]));
      // const link = document.createElement('a');
      // link.href = url;
      // link.setAttribute('download', fileName);
      // document.body.appendChild(link);
      // link.click();
      // 
      // // Clean up
      // window.URL.revokeObjectURL(url);
      // document.body.removeChild(link);
    } catch (err) {
      console.error('Error downloading file:', err);
      alert('Failed to download file: ' + err.message);
    }
  };
  
  const toggleSortDirection = () => {
    setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
  };
  
  const handleSort = (field) => {
    if (sortBy === field) {
      toggleSortDirection();
    } else {
      setSortBy(field);
      setSortDirection('asc');
    }
  };
  
  const toggleFilters = () => {
    setVisibleFilters(!visibleFilters);
  };
  
  // Filter files based on search term
  const filteredFiles = files.filter(file => {
    return searchTerm 
      ? file.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (file.creatorName && file.creatorName.toLowerCase().includes(searchTerm.toLowerCase()))
      : true;
  });
  
  // Sort files
  const sortedFiles = [...filteredFiles].sort((a, b) => {
    let comparison = 0;
    
    if (sortBy === 'name') {
      comparison = a.name.localeCompare(b.name);
    } else if (sortBy === 'date') {
      comparison = new Date(a.updatedAt) - new Date(b.updatedAt);
    } else if (sortBy === 'creator') {
      comparison = (a.creatorName || '').localeCompare(b.creatorName || '');
    }
    
    return sortDirection === 'asc' ? comparison : -comparison;
  });
  
  const handleSelectFile = (file) => {
    setSelectedFile(file);
  };
  
  const handleCloseFileDetails = () => {
    setSelectedFile(null);
  };
  
  const handleCreateFolder = (folderId, folderName, parentFolderId) => {
    console.log(`Created folder: ${folderName} (ID: ${folderId}) under parent: ${parentFolderId}`);
    // In a real implementation, this would call the backend API
  };
  
  const handleRenameFolder = (folderId, newName) => {
    console.log(`Renamed folder ${folderId} to: ${newName}`);
    // In a real implementation, this would call the backend API
  };
  
  const handleDeleteFolder = (folderId) => {
    console.log(`Deleted folder: ${folderId}`);
    // In a real implementation, this would call the backend API
  };
  
  const handleMoveFile = (fileId, sourceFolderId, targetFolderId) => {
    console.log(`Moved file ${fileId} from ${sourceFolderId} to ${targetFolderId}`);
    // In a real implementation, this would call the backend API
  };
  
  const handleApplySuggestions = (suggestion) => {
    console.log('Applying AI suggestion:', suggestion);
    // In a real implementation, this would call the backend API to apply the changes
    
    // Example handling based on suggestion type
    if (suggestion.type === 'create_folder') {
      console.log(`Creating folder ${suggestion.folderName} under ${suggestion.parentFolder}`);
    } else if (suggestion.type === 'reorganize') {
      console.log('Reorganizing files into folders:', suggestion.structure);
    } else if (suggestion.type === 'move_files') {
      console.log(`Moving files to ${suggestion.targetFolder}:`, suggestion.files);
    }
  };
  
  if (loading) {
    return (
      <div className="loading-container">
        <FaSync className="fa-spin" />
        <p>Loading files...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="error-container">
        <FaExclamationCircle />
        <h2>Error Loading Files</h2>
        <p>{error}</p>
        <button onClick={fetchFiles} className="retry-button">Retry</button>
      </div>
    );
  }

  // If a file is selected, show file details
  if (selectedFile) {
    return (
      <FileDetails 
        file={selectedFile}
        onClose={handleCloseFileDetails}
        onDownload={handleDownloadFile}
      />
    );
  }
  
  return (
    <div className="files-container">
      <SearchBar onSearch={setSearchTerm} />
      
      <div className="files-view-selector">
        <button 
          className={`view-selector-btn ${viewMode === 'list' ? 'active' : ''}`}
          onClick={() => setViewMode('list')}
        >
          <FaList /> List View
        </button>
        <button 
          className={`view-selector-btn ${viewMode === 'folders' ? 'active' : ''}`}
          onClick={() => setViewMode('folders')}
        >
          <FaFolderOpen /> Folders
        </button>
        <button 
          className={`view-selector-btn ${viewMode === 'ai' ? 'active' : ''}`}
          onClick={() => setViewMode('ai')}
        >
          <FaRobot /> AI Organizer
        </button>
      </div>
      
      {/* List View */}
      {viewMode === 'list' && (
        <div className="file-list-container">
          <div className="file-list-header">
            <h2>Your Files</h2>
            <button className="filter-toggle" onClick={toggleFilters}>
              <FaFilter /> Filters
            </button>
          </div>
          
          {visibleFilters && (
            <div className="filters">
              <button 
                className={filter === 'all' ? 'active' : ''} 
                onClick={() => setFilter('all')}
              >
                All Files
              </button>
              <button 
                className={filter === 'models' ? 'active' : ''} 
                onClick={() => setFilter('models')}
              >
                3D Models
              </button>
              <button 
                className={filter === 'archives' ? 'active' : ''} 
                onClick={() => setFilter('archives')}
              >
                Archives
              </button>
              <button 
                className={filter === 'documents' ? 'active' : ''} 
                onClick={() => setFilter('documents')}
              >
                Documents
              </button>
            </div>
          )}
          
          {sortedFiles.length === 0 ? (
            <div className="no-files">
              <p>No files found.</p>
              {searchTerm && <p>Try a different search term or clear your search.</p>}
            </div>
          ) : (
            <div className="files-table-wrapper">
              <table className="files-table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort('name')} className={sortBy === 'name' ? 'sorting-active' : ''}>
                      Name {sortBy === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('creator')} className={sortBy === 'creator' ? 'sorting-active' : ''}>
                      Creator {sortBy === 'creator' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('date')} className={sortBy === 'date' ? 'sorting-active' : ''}>
                      Updated {sortBy === 'date' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedFiles.map(file => (
                    <tr key={file.originalId} onClick={() => handleSelectFile(file)} style={{cursor: 'pointer'}}>
                      <td className="file-name" title={file.name}>
                        <div className="file-type-icon">{getFileTypeIcon(file.name)}</div>
                        {file.name}
                      </td>
                      <td>{file.creatorName || '-'}</td>
                      <td>{new Date(file.updatedAt).toLocaleDateString()}</td>
                      <td className="actions-cell" onClick={(e) => e.stopPropagation()}>
                        <button 
                          onClick={() => handleDownloadFile(file.originalId, file.name)}
                          className="download-button"
                          title="Download this file"
                        >
                          <FaDownload /> Download
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      
      {/* Folder View */}
      {viewMode === 'folders' && (
        <FolderStructure 
          files={sortedFiles}
          onSelectFile={handleSelectFile}
          onMoveFile={handleMoveFile}
          onCreateFolder={handleCreateFolder}
          onDeleteFolder={handleDeleteFolder}
          onRenameFolder={handleRenameFolder}
        />
      )}
      
      {/* AI Organizer View */}
      {viewMode === 'ai' && (
        <AIOrganizer 
          onApplySuggestions={handleApplySuggestions}
        />
      )}
    </div>
  );
};

export default FileList;